import _ from "lodash";

import { DeferredReadyMixin } from "@/helpers/Maps/deferredReady.js";
import eventsBinder from "@/helpers/Maps/eventsBinder.js";
import getPropsMixin from "@/helpers/Maps/getPropsValuesMixin.js";
import loadedGoogleMapsAPI from "@/helpers/Maps/mapsLazyLoader.js";
import mountableMixin from "@/helpers/Maps/mountableMixin.js";
import propsBinder from "@/helpers/Maps/propsBinder.js";
//import { MarkerClusterer } from "@googlemaps/markerclusterer";

const props = {
    center: {
        required: true,
        twoWay: true,
        type: Object
    },
    zoom: {
        required: false,
        twoWay: true,
        type: Number
    },
    heading: {
        type: Number,
        twoWay: true
    },
    mapTypeId: {
        twoWay: true,
        type: String
    },
    mapId: {
        type: String,
        default: function () {
            return "a5833a605c55cbfa";
        }
    },
    bounds: {
        twoWay: true,
        type: Object
    },
    tilt: {
        twoWay: true,
        type: Number
    },
    options: {
        type: Object,
        default() {
            return {};
        }
    },
};

const events = [
    "click",
    "dblclick",
    "drag",
    "dragend",
    "dragstart",
    "idle",
    "mousemove",
    "mouseout",
    "mouseover",
    "resize",
    "rightclick",
    "tilesloaded"
];

// Plain Google Maps methods exposed here for convenience
const linkedMethods = _.fromPairs(
    ["panBy", "panTo", "panToBounds", "fitBounds"].map(methodName => [
        methodName,
        function () {
            if (this.$mapObject) this.$mapObject[methodName].apply(this.$mapObject, arguments);
        }
    ])
);

// Other convenience methods exposed by Vue Google Maps
const customMethods = {
    resize() {
        if (this.$mapObject) {
            window.google.maps.event.trigger(this.$mapObject, "resize");
        }
    },
    resizePreserveCenter() {
        if (!this.$mapObject) return;

        const oldCenter = this.$mapObject.getCenter();
        window.google.maps.event.trigger(this.$mapObject, "resize");
        this.$mapObject.setCenter(oldCenter);
    },

    /// Override mountableMixin::_resizeCallback
    /// because resizePreserveCenter is usually the
    /// expected behaviour
    _resizeCallback() {
        this.resizePreserveCenter();
    }
};

// Methods is a combination of customMethods and linkedMethods
const methods = _.assign({}, customMethods, linkedMethods);
export default {
    mixins: [getPropsMixin, DeferredReadyMixin, mountableMixin],
    props: props,
    replace: false, // necessary for css styles

    created() {
        this.$mapCreated = new Promise((resolve, reject) => {
            this.$mapCreatedDeferred = { resolve, reject };
        });

        const updateCenter = () => {
            if (!this.$mapObject) return;

            this.$mapObject.panTo({
                lat: this.finalLat,
                lng: this.finalLng
            });
        };
        this.$watch("finalLat", updateCenter);
        this.$watch("finalLng", updateCenter);
    },
    computed: {
        finalLat() {
            return this.center && typeof this.center.lat === "function" ? this.center.lat() : this.center.lat;
        },
        finalLng() {
            return this.center && typeof this.center.lng === "function" ? this.center.lng() : this.center.lng;
        }
    },

    deferredReady() {
        return loadedGoogleMapsAPI(this.$store.state.GlobalSettings.Maps.GoogleMapsApiKey)
            .then(() => {
                // getting the DOM element where to create the map
                const element = this.$refs["vue-map"];

                // creating the map
                const copiedData = _.clone(this.getPropsValues());
                delete copiedData.options;
                const options = _.clone(this.options);
                _.assign(options, copiedData);
                this.$mapObject = new window.google.maps.Map(element, options);

                // binding properties (two and one way)
                propsBinder(this, this.$mapObject, _.omit(props, ["center", "zoom", "bounds"]));

                // manually trigger center and zoom
                this.$mapObject.addListener("center_changed", () => {
                    this.$emit("center_changed", this.$mapObject.getCenter());
                });
                this.$mapObject.addListener("zoom_changed", () => {
                    this.$emit("zoom_changed", this.$mapObject.getZoom());
                });
                this.$mapObject.addListener("bounds_changed", () => {
                    this.$emit("bounds_changed", this.$mapObject.getBounds());
                });
                this.$mapObject.addListener("idle", () => {
                    const center = this.$mapObject.getCenter(); // LatLng object
                    const bounds = this.$mapObject.getBounds(); // LatLngBounds object
                    const ne = bounds.getNorthEast(); // northeast corner of the visible area
                    const radiusInMeters = window.google.maps.geometry.spherical.computeDistanceBetween(center, ne);

                    //console.log("Center:", center.lat(), center.lng());
                    //console.log("Approx. Radius (m):", radiusInMeters);
                    this.$emit("idle_2", {
                        center: { lat: center.lat(), lng: center.lng() },
                        radius: radiusInMeters
                    });
                });

                //binding events
                eventsBinder(this, this.$mapObject, events);
                //console.log("MAP Created with props:", this.$props);

                /* //we use advanced markers, these are not supported for clustering yet
                this.$clusterObject = new MarkerClusterer({ map: this.$mapObject, markers: [] });
                */

                this.$mapCreatedDeferred.resolve(this.$mapObject);
                return this.$mapCreated;
            })
            .catch(error => {
                throw error;
            });
    },
    watch: {
        zoom(zoom) {
            if (this.$mapObject) {
                this.$mapObject.setZoom(zoom);
            }
        },
    },
    methods: methods
};
